import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Chat.css';  // Add this import line

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [informationPosition, setinformationPosition] = useState({ x: 20, y: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [informations, setinformations] = useState([]); // Replace showinformation with informations array
  const [activeinformationId, setActiveinformationId] = useState(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    // Add user message to chat
    const userMessage = { role: 'user', content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch('https://gpt.wordpad.workers.dev/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            { role: 'system', content: 'You are a helpful assistant.' },
            ...messages,
            userMessage
          ],
          functions: [
            {
              name: "display_university_information",
              description: "Displays information about university for a given university name",
              parameters: {
                type: "object",
                properties: {
                  universityName: {
                    type: "string",
                    description: "The name of the university to display information about"
                  }
                },
                required: ["universityName"]
              }
            }
          ],
          function_call: "auto"  // Allow the model to decide when to call the function
        })
      });

      const data = await response.json();

      // Check if there's a function call in the response
      if (data.choices[0].message.function_call) {
        const functionCall = data.choices[0].message.function_call;
        
        if (functionCall.name === 'display_university_information') {
          const args = JSON.parse(functionCall.arguments);
          createinformationWindow(args.universityName);
        }
      } else {
        // Handle regular assistant response
        setMessages(prev => [...prev, { role: 'assistant', content: data.choices[0].message.content }]);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, { role: 'system', content: 'Error: Failed to get response' }]);
    }
    setIsLoading(false);
  };

  // Update handleMouseDown to handle both mouse and touch events
  const handleMouseDown = (e, informationId) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const information = informations.find(t => t.id === informationId);
    if (information) {
      setIsDragging(true);
      setActiveinformationId(informationId);
      setDragOffset({
        x: clientX - information.position.x,
        y: clientY - information.position.y
      });
    }
  };

  // Update handleMouseMove to handle both mouse and touch events
  const handleMouseMove = useCallback((e) => {
    if (isDragging && activeinformationId) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      
      setinformations(prev => prev.map(information => {
        if (information.id === activeinformationId) {
          return {
            ...information,
            position: {
              x: clientX - dragOffset.x,
              y: clientY - dragOffset.y
            }
          };
        }
        return information;
      }));
    }
  }, [isDragging, activeinformationId, dragOffset]);

  const handleMouseUp = () => {
    setIsDragging(false);
    setActiveinformationId(null);
  };

  // Update the event listeners in useEffect
  useEffect(() => {
    const handleMouseMoveEvent = (e) => handleMouseMove(e);
    const handleMouseUpEvent = handleMouseUp;

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMoveEvent);
      window.addEventListener('mouseup', handleMouseUpEvent);
      window.addEventListener('touchmove', handleMouseMoveEvent);
      window.addEventListener('touchend', handleMouseUpEvent);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMoveEvent);
      window.removeEventListener('mouseup', handleMouseUpEvent);
      window.removeEventListener('touchmove', handleMouseMoveEvent);
      window.removeEventListener('touchend', handleMouseUpEvent);
    };
  }, [isDragging, handleMouseMove]);

  // Add new function to create information windows
  const createinformationWindow = async (universityName) => {
    const newinformation = {
      id: Date.now(),
      position: { x: 20, y: 200 },
      universityName: universityName,
      content: 'Loading university information...' // Add temporary loading message
    };
    setinformations(prev => [...prev, newinformation]);

    try {
      const response = await fetch('https://gpt.wordpad.workers.dev/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { 
              role: 'system', 
              content: 'Please provide a brief bullet-point overview of the specified university in valid HTML format. Use `<ul>` for the list and `<li>` for each bullet point. Do not include any other text or formatting.'

            },
            {
              role: 'user',
              content: `Please provide information about ${universityName}`
            }
          ]
        })
      });

      const data = await response.json();
      const universityInfo = data.choices[0].message.content;

      // Update the information window with the fetched content
      setinformations(prev => prev.map(info => {
        if (info.id === newinformation.id) {
          return {
            ...info,
            content: universityInfo
          };
        }
        return info;
      }));
    } catch (error) {
      console.error('Error fetching university information:', error);
      // Update with error message if fetch fails
      setinformations(prev => prev.map(info => {
        if (info.id === newinformation.id) {
          return {
            ...info,
            content: 'Error: Failed to fetch university information'
          };
        }
        return info;
      }));
    }
  };

  return (
    <>
      <div className="chat-container">
        <div className="chat-messages">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`message ${message.role === 'user' ? 'user-message' : 'system-message'}`}
            >
              {message.content}
            </div>
          ))}
          {isLoading && <div className="system-message">Loading...</div>}
          <div ref={messagesEndRef} />
        </div>
        <form className="chat-input" onSubmit={handleSubmit}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type your message..."
            disabled={isLoading}
          />
          <button type="submit" disabled={isLoading}>
            Send
          </button>
        </form>
      </div>
      
      {informations.map(information => (
        <div 
          key={information.id}
          className="information-overlay"
          style={{
            left: `${information.position.x}px`,
            top: `${information.position.y}px`
          }}
          onMouseDown={(e) => handleMouseDown(e, information.id)}
          onTouchStart={(e) => handleMouseDown(e, information.id)}
        >
          <div className="information-header">
            <span>{information.universityName}</span>
            <button 
              className="close-button"
              onClick={() => setinformations(prev => prev.filter(t => t.id !== information.id))}
            >
              ×
            </button>
          </div>
          <div 
            className="information-content"
            dangerouslySetInnerHTML={{ __html: information.content || 'Loading...' }}
          />
        </div>
      ))}
    </>
  );
};

export default Chat;
